import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ConfirmDeleteModal } from '../../components/common/ConfirmDeleteModal';
import {
  fetchWorkflowTemplatesList,
  deleteWorkflowTemplate,
  duplicateWorkflowTemplate,
  hydrateDuplicatedWorkflowTemplate,
  type WorkflowTemplateListItem,
} from '../../services/workflow';
import { getFileCategories, getSubFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import { listIntranetOfficerOptions, type IntranetOfficerOption } from '../../services/userAdministration';
import { PermissionGate } from '../../rbac/guards';
import { fltSelClass } from '../../utils/alSelClass';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';

const SORT_FIELD_MAP: Record<string, string> = {
  updatedByName: 'updatedBy',
};

export default function WorkflowTemplatesPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  // const userId = user?.id ?? '';

  const [draftTemplateName, setDraftTemplateName] = useState('');
  const [draftFileCategory, setDraftFileCategory] = useState('');
  const [draftSubFileCategory, setDraftSubFileCategory] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [draftOfficer, setDraftOfficer] = useState('');

  const [templateName, setTemplateName] = useState('');
  const [fileCategory, setFileCategory] = useState('');
  const [subFileCategory, setSubFileCategory] = useState('');
  const [status, setStatus] = useState('');
  const [officer, setOfficer] = useState('');

  const [pageSize, setPageSize] = useState(10);
  const [uiPage, setUiPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [rows, setRows] = useState<WorkflowTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<WorkflowTemplateListItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const [reloadToken, setReloadToken] = useState(0);

  const [sortKey, setSortKey] = useState('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [fileCategoryOptions, setFileCategoryOptions] = useState<LookupItem[]>([]);
  const [subFileCategoryOptions, setSubFileCategoryOptions] = useState<LookupItem[]>([]);
  const [officerOptions, setOfficerOptions] = useState<IntranetOfficerOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    async function loadOptions() {
      setOptionsLoading(true);
      const [cats, officers] = await Promise.all([getFileCategories(), listIntranetOfficerOptions()]);
      if (!alive) return;
      if (cats.success) setFileCategoryOptions(cats.data);
      if (officers.success) setOfficerOptions(officers.data);
      setOptionsLoading(false);
    }
    void loadOptions();
    return () => { alive = false; };
  }, [user?.id]);

  useEffect(() => {
    let alive = true;
    async function loadSubCategories() {
      const selected = fileCategoryOptions.find((c) => c.name === draftFileCategory);
      if (!selected) {
        if (alive) setSubFileCategoryOptions([]);
        return;
      }
      const res = await getSubFileCategories(selected.id);
      if (!alive) return;
      if (res.success) setSubFileCategoryOptions(res.data);
      else setSubFileCategoryOptions([]);
    }
    void loadSubCategories();
    return () => {
      alive = false;
    };
  }, [draftFileCategory, fileCategoryOptions]);

  useEffect(() => {
    
    if (!user?.id) return;
    let alive = true;
    async function run() {
      setLoading(true);
      const res = await fetchWorkflowTemplatesList({
        pageNumber: Math.max(0, uiPage - 1),
        pageSize,
        workflowTemplateName: templateName.trim() || undefined,
        fileCategory: fileCategory.trim() || undefined,
        subFileCategory: subFileCategory.trim() || undefined,
        status: status || undefined,
        allocationOfficerName: officer.trim() || undefined,
        sortBy: SORT_FIELD_MAP[sortKey] ?? sortKey,
        sortDirection: sortDir.toUpperCase(),
      });
      if (!alive) return;
      if (!res.success) {
        setFlashToast({ message: res.message, variant: 'error' });
        setRows([]);
        setTotalElements(0);
        setTotalPages(1);
      } else {
        setRows(res.data.items);
        setTotalElements(res.data.totalElements);
        setTotalPages(res.data.totalPages);
      }
      setLoading(false);
    }
    void run();
    return () => { alive = false; };
  }, [user?.id, templateName, fileCategory, subFileCategory, status, officer, uiPage, pageSize, reloadToken, sortKey, sortDir]);

  const columns = useMemo<Column<WorkflowTemplateListItem>[]>(() => {
    return [
      { key: 'workflowCode', header: t('workflow.templates.workflowCode'), getValue: (r) => r.workflowCode },
      {
        key: 'workflowTemplateName',
        header: t('workflow.templates.workflowTemplateName'),
        getValue: (r) => r.workflowTemplateName,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.workflowTemplateName}</span>,
      },
      { key: 'fileCategory', header: t('admin.shared.fileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('admin.shared.subFileCategory'), getValue: (r) => r.subFileCategory },
      { key: 'version', header: t('workflow.templates.version'), getValue: (r) => r.version ?String(r.version) :''},
      {
        key: 'status',
        header: t('common.status'),
        getValue: (r) => r.status,
        render: (r) => {
          const cls = r.status === 'Active' ? 'p-com' : r.status === 'Draft' ? 'p-pend' : 'p-arc';
          return <span className={`pill ${cls}`}>{r.status}</span>;
        },
      },
      { key: 'allocationOfficerName', header: t('workflow.templates.allocationOfficer'), getValue: (r) => r.allocationOfficerName },
      { key: 'updatedByName', header: t('workflow.templates.lastUpdatedBy'), getValue: (r) => r.updatedByName },
      {
        key: 'updatedAt',
        header: t('workflow.templates.lastUpdatedAt'),
        getValue: (r) => r.updatedAt,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{formatDocumentTableDateTime(r.updatedAt)}</span>,
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            {r.status === 'Active' ? (
              <button
                type="button"
                className="act-b"
                data-tip={t('workflow.templates.editTemplate')}
                aria-label={t('workflow.templates.editTemplate')}
                onClick={() => nav(`/workflow/templates/designer?templateId=${encodeURIComponent(r.id)}&mode=edit`)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="act-b"
                data-tip={t('workflow.templates.viewTemplate')}
                aria-label={t('workflow.templates.viewTemplate')}
                onClick={() => nav(`/workflow/templates/designer?templateId=${encodeURIComponent(r.id)}&mode=view`)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            )}
            {r.status === 'Draft' ? (
              <button
                type="button"
                className="act-b"
                data-tip={t('workflow.templates.editTemplate')}
                aria-label={t('workflow.templates.editTemplate')}
                onClick={() => nav(`/workflow/templates/designer?templateId=${encodeURIComponent(r.id)}&mode=edit`)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            ) : r.status !== 'Active' ? (
              <div><br /></div>
            ) : null}
            <button
              type="button"
              className="act-b"
              data-tip={duplicatingId === r.id ? t('workflow.templates.duplicating') : t('workflow.templates.duplicateTemplate')}
              aria-label={t('workflow.templates.duplicateTemplate')}
              disabled={duplicatingId !== null}
              onClick={async () => {
                if (duplicatingId !== null) return;
                setDuplicatingId(r.id);
                const res = await duplicateWorkflowTemplate(r.id);
                if (!res.success) {
                  setDuplicatingId(null);
                  setFlashToast({ message: res.message, variant: 'error' });
                  return;
                }

                const hydrateRes = await hydrateDuplicatedWorkflowTemplate(r.id, res.data.id);
                setDuplicatingId(null);
                if (!hydrateRes.success) {
                  setFlashToast({ message: hydrateRes.message, variant: 'error' });
                  return;
                }

                nav(`/workflow/templates/designer?templateId=${encodeURIComponent(res.data.id)}&mode=edit`);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
            {r.status === 'Draft' ? (
              <button
                type="button"
                className="act-b"
                data-tip={t('workflow.templates.deleteTemplate')}
                aria-label={t('workflow.templates.deleteTemplate')}
                disabled={deletingId === r.id}
                style={{ color: 'var(--red)' }}
                onClick={() => setConfirmDelete(r)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [deletingId, duplicatingId, nav, t]);

  return (
    <>
      <div className="bc">
        <span>{t('workflow.management')}</span>
        <span className="bc-sep">/</span>{t('workflow.templates.breadcrumb')}
      </div>

      <div className="page-hdr" style={{ marginBottom: 12 }}>
        <div className="pg-title" style={{ marginBottom: 0 }}>
          {t('workflow.templates.title')}
        </div>
        <PermissionGate requirement={{ anyPermissions: ['CREATE_WORKFLOW_TEMPLATE'] }}>
          <Link to="/workflow/templates/designer?mode=create" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('workflow.templates.createNew')}
          </Link>
        </PermissionGate>
      </div>

      <div className="flt-card">
        <div className="flt-grid">
          <div className="flt-f">
            <div className="flt-l">{t('workflow.templates.templateName')}</div>
            <input
              className="flt-inp"
              value={draftTemplateName}
              onChange={(e) => setDraftTemplateName(e.target.value)}
              placeholder={t('workflow.templates.templateNamePlaceholder')}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.fileCategory')}</div>
            <select
              className={fltSelClass(draftFileCategory)}
              value={draftFileCategory}
              disabled={optionsLoading}
              onChange={(e) => {
                setDraftFileCategory(e.target.value);
                setDraftSubFileCategory('');
              }}
            >
              <option value="" disabled hidden>
                {t('workflow.templates.selectFileCategory')}
              </option>
              {fileCategoryOptions.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.subFileCategory')}</div>
            <select
              className={fltSelClass(draftSubFileCategory)}
              value={draftSubFileCategory}
              onChange={(e) => setDraftSubFileCategory(e.target.value)}
              disabled={!draftFileCategory}
            >
              <option value="" disabled hidden>
                {t('workflow.templates.selectSubFileCategory')}
              </option>
              {subFileCategoryOptions.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('common.status')}</div>
            <select className={fltSelClass(draftStatus)} value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)}>
              <option value="" disabled hidden>
                {t('workflow.templates.selectStatus')}
              </option>
              <option value="Active">{t('common.active')}</option>
              <option value="Inactive">{t('common.inactive')}</option>
              <option value="Draft">{t('legalKnowledge.status.draft')}</option>
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('workflow.templates.allocationOfficer')}</div>
            <select
              className={fltSelClass(draftOfficer)}
              value={draftOfficer}
              disabled={optionsLoading}
              onChange={(e) => setDraftOfficer(e.target.value)}
            >
              <option value="" disabled hidden>
                {t('workflow.templates.selectAllocationOfficer')}
              </option>
              {officerOptions.map((o) => (
                <option key={`${o.displayName}|${o.division}`} value={o.displayName}>
                  {o.division ? `${o.displayName} (${o.division})` : o.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setDraftTemplateName('');
              setDraftFileCategory('');
              setDraftSubFileCategory('');
              setDraftStatus('');
              setDraftOfficer('');
              setTemplateName('');
              setFileCategory('');
              setSubFileCategory('');
              setStatus('');
              setOfficer('');
              setUiPage(1);
              setPageSize(10);
              setReloadToken((t) => t + 1);
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setTemplateName(draftTemplateName);
              setFileCategory(draftFileCategory);
              setSubFileCategory(draftSubFileCategory);
              setStatus(draftStatus);
              setOfficer(draftOfficer);
              setUiPage(1);
              setReloadToken((t) => t + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      <DataTable
        title={t('workflow.templates.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[10, 20, 50]}
        serverPaging={{
          totalCount: totalElements,
          totalPages: totalPages,
          page: uiPage,
          pageSize: pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
        serverSort={{
          key: sortKey,
          dir: sortDir,
          onSortChange: (key, dir) => {
            setSortKey(key);
            setSortDir(dir);
            setUiPage(1);
          },
        }}
      />

      <ConfirmDeleteModal
        open={!!confirmDelete}
        title={t('workflow.templates.deleteTitle')}
        entityLabel={t('workflow.templates.entityLabel')}
        entityName={confirmDelete?.workflowTemplateName ?? ''}
        deleting={!!deletingId}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          setDeletingId(confirmDelete.id);
          const res = await deleteWorkflowTemplate(confirmDelete.id);
          setDeletingId(null);
          setConfirmDelete(null);
          if (res.success) {
            setReloadToken((t) => t + 1);
          } else {
            setFlashToast({ message: res.message, variant: 'error' });
          }
        }}
      />
    </>
  );
}
